/**
 * Slim banner shown above the app shell when the session is acting
 * as a VAU. Built from primitives (`Box` + `Flex`) rather than
 * Axiom's `Alert` because Alert centers and stacks its content —
 * we want left-aligned, single-line so the banner stays one row tall.
 *
 * Reads ``useVAUContext`` and renders when ``is_assumed`` is true —
 * or, as a fail-safe, when the context probe errored and we can't
 * rule out that the session is still assumed. "Switch back" calls
 * the release mutation; the hook's onSuccess does the hard reload
 * (cache eviction + full-document navigation).
 */

import { IconCircleInfo } from '@optiaxiom/icons';
import { Avatar, Box, Button, Flex, Text } from '@optiaxiom/react';

import { useReleaseVAU, useVAUContext } from '@frontend/features/vau/hooks/useVAUContext';

export const AssumedVAUBanner = () => {
  const { data, isError } = useVAUContext();
  const release = useReleaseVAU();

  const isAssumed = data?.is_assumed ?? false;

  // Fail-safe: if the assumed-context probe errors we can't confirm this
  // session is a VAU — but we can't confirm it isn't either, and the caller
  // may still be assumed server-side. Keep the banner up so the user never
  // silently loses the "acting as a VAU" indication (and can always switch
  // back). Mirrors useVauSwitchGuard's treat-error-as-may-be-assumed.
  if (!isAssumed && !isError) return null;

  return (
    <Box
      role="status"
      px="16"
      py="4"
      borderB="1"
      borderColor="border.default"
      style={{ backgroundColor: 'rgba(56, 121, 217, 0.10)' }}
    >
      <Flex flexDirection="row" alignItems="center" gap="8">
        <IconCircleInfo />
        {isAssumed ? (
          <>
            {/* The mock reads "Acting as [avatar] Mortimer." — the avatar sits
                inside the sentence, between the verb and the name, not out at
                the front of the row. Split across three children so the
                Flex gap does the spacing and the avatar keeps its own box.
                Initials only, the same treatment IM gives a VAU
                (ConversationAvatar / SenderAvatar), so the person reads the
                same wherever they appear. */}
            <Text fontSize="sm">Acting as</Text>
            <Avatar name={data?.vau_name} size="2xs" />
            <Text fontSize="sm">
              <strong>{data?.vau_name}</strong>. All actions will be taken as {data?.vau_name}.
            </Text>
          </>
        ) : (
          <Text fontSize="sm">
            You may still be acting as a VAU — we couldn&apos;t confirm your current identity.
            Switch back to be sure.
          </Text>
        )}
        <Button
          appearance="default"
          size="sm"
          onClick={() => release.mutate({})}
          disabled={release.isPending}
        >
          Switch back
        </Button>
      </Flex>
    </Box>
  );
};
